import React, { useContext, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import { ColorModeContext } from '../../theme';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import * as Sentry from '@sentry/browser';

import { ReactComponent as BurgerMenuIcon } from '../../assets/img/icons/menu-2.svg';
import { ReactComponent as DashboardIcon } from '../../assets/icons/base/home.svg';
import { ReactComponent as RealTimeViewIcon } from '../../assets/icons/base/radio.svg';
import { ReactComponent as ChartsIcon } from '../../assets/icons/base/pie-chart.svg';
// import { ReactComponent as SearchIcon } from '../../assets/icons/base/search.svg';
// import { ReactComponent as AnalyticsIcon } from '../../assets/icons/base/trending-up.svg';
import { ReactComponent as ConfigurationIcon } from '../../assets/icons/base/sliders.svg';
import { ReactComponent as ViprSmall } from '../../assets/img/icons/vipr-small.svg';
import { ReactComponent as NotificationsNoneIcon } from '../../assets/icons/base/bell.svg';
import { ReactComponent as MoonIcon } from '../../assets/icons/base/moon.svg';
import { ReactComponent as SunIcon } from '../../assets/icons/base/sun.svg';
import { ReactComponent as FireIcon } from '../../assets/icons/base/fire.svg';
import { ReactComponent as LogoutIcon } from '../../assets/icons/base/log-out.svg';
import { Avatar, Typography, IconButton, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { logoutUser } from '../../redux/slices/userProfile';
import { adminPermission, chartsPermission, rtvPermission, searchPermission } from '../../constants/userPermissions';

import { SidebarStyled, ListItemButtonStyled, DrawerHeader, ListItemIconStyled, ListItemTextStyled } from './Sidebar.styled';
import HotlistModal, { HotlistModalRefType } from '../HotlistModal';
import { errorDark } from '../../theme/colors';


const sidebarList = [
  {
    pageName: 'Dashboard',
    link: '/dashboard',
    icon: <DashboardIcon />,
    permissionName: searchPermission,
  },
  {
    pageName: 'Real time view',
    link: '/real-time-view',
    icon: <RealTimeViewIcon />,
    permissionName: rtvPermission,
  },
  {
    pageName: 'Charts',
    link: '/charts',
    icon: <ChartsIcon />,
    permissionName: chartsPermission,
  },
  // {
  //   pageName: 'Search',
  //   link: '/search',
  //   icon: <SearchIcon />,
  //   permissionName: '',
  // },
  // {
  //   pageName: 'Analytics',
  //   link: '/analytics',
  //   icon: <AnalyticsIcon />,
  //   permissionName: '',
  // },
  {
    pageName: 'Configuration',
    link: '/configuration',
    icon: <ConfigurationIcon />,
    permissionName: adminPermission,
  },
];

export const Sidebar = (props: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((store: RootState) => store?.user?.user);
  const { children } = props;
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const colorMode = useContext(ColorModeContext);
  const hotlistModal = useRef<HotlistModalRefType>(null);

  const handleSidebar = () => {
    setOpen(!open);
  };

  const handleChangeMode = () => {
    colorMode.toggleColorMode();

    // Inverse currentColorMode
    localStorage.setItem('colorMode', theme.palette.mode === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      window?.dispatchEvent((new CustomEvent('resize')));
    }, 50);
    return () => clearTimeout(timeout);
  }, [open]);

  const handleLogout = () => {
    dispatch(logoutUser());
    Sentry.setUser(null);
    navigate('/');
  };
  const handleKeyUp = React.useCallback((event: KeyboardEvent) => {
    if ((event.key === 'h' || event.key === 'H')
      && document.activeElement?.tagName && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)
      && ['/real-time-view', '/dashboard'].includes(location.pathname)
      && !hotlistModal.current?.isOpen
    ) {
      event.preventDefault();
      hotlistModal.current?.openModal();
    }
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener('keypress', handleKeyUp);
    return () => window.removeEventListener('keypress', handleKeyUp);
  }, []);

  const permissionsActions = user?.permissions.map((perm) => perm.action) || [];
  const filteredSidebarList = sidebarList.filter((sl) => (permissionsActions.includes(sl.permissionName) || permissionsActions.includes(adminPermission)));

  return (
    <Box
      sx={{ display: 'flex', overflowX: 'hidden' }}
      width="100vw"
      minHeight="100vh"
    >
      <CssBaseline />
      <HotlistModal ref={hotlistModal} />
      <SidebarStyled
        ref={props.refrence}
        sx={{ bgcolor: 'base.background' }}
        variant="permanent"
        open={open}
      >
        <DrawerHeader sx={{ justifyContent: open ? 'flex-start' : 'center' }}>
          <IconButton
            sx={{ padding: '0px' }}
            onClick={handleSidebar}>
            <ViprSmall fill={theme.palette.primary[900]} stroke={'transparent'} />
          </IconButton>
          {open
            && <Box
              display={'flex'}
              sx={{ ml: 1 }}
              flex={1}
              alignItems={'center'}
            >
              <Typography variant="h6" color="textPrimary">VIPR.ai</Typography>
              <IconButton
                sx={{ marginLeft: 'auto' }}
                onClick={handleSidebar}>
                <BurgerMenuIcon stroke={theme.palette.grey[500]} />
              </IconButton>
            </Box>
          }
        </DrawerHeader>
        <Divider variant={'middle'} />
        <List sx={{ justifyContent: 'center' }}>
          {filteredSidebarList.map((item, index) => (
            <ListItemButtonStyled
              onClick={() => navigate(item.link)}
              sx={{ bgcolor: location.pathname === item.link ? 'primary.200' : 'transparent' }}
              key={index + 1}
              id={`navigate-to-${item.pageName}`}
            >
              <ListItemIconStyled open={open}>
                {item.icon}
              </ListItemIconStyled>
              <ListItemTextStyled primary={item.pageName} open={open} />
            </ListItemButtonStyled>
          ))}
        </List>
        <List sx={{ marginTop: 'auto' }}>
          <ListItemButton
            onClick={handleChangeMode}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2,
              borderRadius: '6px',
            }}
          >
            <ListItemIconStyled open={open}>
              {theme.palette.mode === 'dark' ? <SunIcon /> : <MoonIcon />}
            </ListItemIconStyled>
            <ListItemTextStyled primary={'Switch theme'} open={open} />
          </ListItemButton>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2,
              borderRadius: '6px',
              marginBottom: '8px',
            }}
            onClick={() => navigate('/subscriptions')}
          >
            <ListItemIconStyled open={open}>
              <NotificationsNoneIcon />
            </ListItemIconStyled>
            <ListItemTextStyled primary={'Subscriptions'} open={open} />
          </ListItemButton>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2,
              borderRadius: '6px',
              margin: '16px auto 8px auto',
              backgroundColor: 'error.100',
              span: { color: `${errorDark[400]} !important` },
            }}
            onClick={() => hotlistModal.current?.openModal()}
          >
            <ListItemIconStyled open={open}>
              <FireIcon style={{ stroke: errorDark[400] }} />
            </ListItemIconStyled>
            <ListItemTextStyled primary={'Quick Hotlist Entry'} open={open} />
          </ListItemButton>
          <Divider variant={'middle'} />
          <Box
            display={'flex'}
            flexDirection={open ? 'row' : 'column'}
            gap={'12px'}
            justifyContent={open ? 'flex-start' : 'center'}
            alignItems={'center'}
            mt={6}
          >
            {!open && (
              <Tooltip
                title={'Logout'}
                placement={'right'}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      color: 'grey.100',
                    },
                  },
                }}
              >
                <IconButton onClick={() => handleLogout()} sx={{ marginLeft: 'unset' }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            )}
            <Avatar
              onClick={() => navigate('/profile')}
              sx={{
                bgcolor: theme.palette.primary[50],
                border: `2px solid ${theme.palette.primary[100]}`,
                color: theme.palette.primary[600],
                borderRadius: '8px',
                fontWeight: '500',
                fontSize: theme.typography.tlg.fontSize,
                cursor: 'pointer',
              }}
            >
              {user && user.first_name.charAt(0) + user.last_name.charAt(0)}
            </Avatar>
            {open
              && <>
                <Typography variant={'tsm'}>
                  {`${user?.first_name} ${user?.last_name}`}
                  <span style={{ fontSize: '14px', lineHeight: '1', color: theme.palette.grey[400], display: 'block' }}>{user?.email}</span>
                </Typography>
                <IconButton onClick={() => handleLogout()} sx={{ marginLeft: 'auto' }}>
                  <LogoutIcon />
                </IconButton>
              </>
            }

          </Box>
        </List>
      </SidebarStyled>
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'primary.background' }}>
        {children}
      </Box>
    </Box>
  );
};

